import React from 'react';
import styles from './styles.module.css';
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css';
import {
  Container, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Backdrop, CircularProgress, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { Alert } from '@material-ui/lab';
import Pagination from '@material-ui/lab/Pagination';
import { Pages } from '../../models/Pages';
import { TwilioPhone, ITwilioPhone, CallFlow } from '../../models/TwilioPhones/TwilioPhone';
import { TwilioPhoneActions, IAxiosTwilioPhones } from '../../actions/TwilioPhoneActions';
import { ITwilioCheckResult } from '../../models/TwilioPhones/TwilioPhone';
import PhoneMaskInput from '../common/PhoneMaskInput';

interface IDepartment { id: number; name: string; }

interface IState {
  phones: TwilioPhone[];
  departments: IDepartment[];
  pagination: Pages | null;
  backdrop: boolean;
  errorAlert: boolean;
  errors: string[];
  filter: TwilioPhonesFilter;
  phoneDisplay: string;
  forwardToDisplay: string;
  checkingId: number | null;
  checkResult: ITwilioCheckResult | null;
  checkPhone: string;
}

class TwilioPhonesFilter {
  phone?: string;
  location?: string;
  forward_to?: string;
  call_flow?: string;
  department_id?: string;
  page?: string;
  per_page: string = '50';

  toString(): string {
    const arr: string[] = [];
    if (this.page) arr.push('page=' + this.page);
    arr.push('per_page=' + this.per_page);
    if (this.phone) arr.push('phone=' + encodeURIComponent(this.phone));
    if (this.location) arr.push('location=' + encodeURIComponent(this.location));
    if (this.forward_to) arr.push('forward_to=' + encodeURIComponent(this.forward_to));
    if (this.call_flow && this.call_flow !== 'ALL') arr.push('call_flow=' + this.call_flow);
    if (this.department_id !== undefined && this.department_id !== '') arr.push('department_id=' + this.department_id);
    return arr.join('&');
  }
}

const FLOW_BADGE: Record<string, string> = {
  DEFAULT:        styles.BadgeDefault,
  CUSTOM:         styles.BadgeCustom,
  EXTERNAL:       styles.BadgeExternal,
  CUSTOM_NEW_FLOW: styles.BadgeCustomNew,
};
const FLOW_LABEL: Record<string, string> = {
  DEFAULT:        'DEFAULT',
  CUSTOM:         'CUSTOM',
  EXTERNAL:       'EXTERNAL',
  CUSTOM_NEW_FLOW: 'CUSTOM NEW',
};

export default class TwilioPhonesComponent extends React.Component<any, IState> {

  state: IState = {
    phones: [],
    departments: [],
    pagination: null,
    backdrop: true,
    errorAlert: false,
    errors: [],
    filter: new TwilioPhonesFilter(),
    phoneDisplay: '',
    forwardToDisplay: '',
    checkingId: null,
    checkResult: null,
    checkPhone: '',
  };

  timeouts: Record<string, number> = {};

  componentDidMount() {
    document.title = 'Twilio Phones';
    this.loadDepartments();
    this.load();
  }

  loadDepartments() {
    TwilioPhoneActions.getDepartments()
      .then((res) => this.setState({ departments: res.data }))
      .catch(() => {});
  }

  load() {
    this.setState({ backdrop: true });
    TwilioPhoneActions.getPhones(this.state.filter.toString())
      .then((data: IAxiosTwilioPhones) => {
        const phones = data.data.data.map((d: ITwilioPhone) => new TwilioPhone(d));
        const pages = new Pages(data.data.per_page, data.data.current_page, data.data.last_page);
        this.setState({ phones, pagination: pages, backdrop: false });
      })
      .catch((err: any) => {
        const errors = err?.response?.data?.errors ?? [err?.response?.data?.error ?? 'Unknown error'];
        this.setState({ errorAlert: true, backdrop: false, errors });
      });
  }

  handleChangePage(page: number) {
    const filter = this.state.filter;
    filter.page = page.toString();
    this.setState({ filter }, () => this.load());
  }

  handlePhoneMask(field: 'phone' | 'forward_to', displayKey: 'phoneDisplay' | 'forwardToDisplay', masked: string, digits: string) {
    const filter = this.state.filter;
    filter[field] = digits || undefined;
    filter.page = undefined;
    this.setState({ filter, [displayKey]: masked } as any);
    if (digits.length === 0 || digits.length === 10) {
      if (this.timeouts[field]) clearTimeout(this.timeouts[field]);
      this.load();
    }
  }

  // Click on forward_to number under CUSTOM — search for that phone as a record
  searchByPhone(phone: string) {
    const digits = phone.replace(/\D/g, '');
    const filter = new TwilioPhonesFilter();
    filter.phone = digits || undefined;
    const masked = digits.length >= 10
      ? `(${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`
      : phone;
    this.setState({ filter, phoneDisplay: masked, forwardToDisplay: '' }, () => this.load());
  }

  // Click on forward_count badge — search all phones that forward TO this phone
  searchForwardedTo(phone: string) {
    const digits = phone.replace(/\D/g, '');
    const filter = new TwilioPhonesFilter();
    filter.forward_to = digits || undefined;
    const masked = digits.length >= 10
      ? `(${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`
      : phone;
    this.setState({ filter, phoneDisplay: '', forwardToDisplay: masked }, () => this.load());
  }

  checkInTwilio(phone: TwilioPhone) {
    this.setState({ checkingId: phone.id });
    TwilioPhoneActions.checkPhone(phone.id)
      .then((res) => {
        this.setState({ checkingId: null, checkResult: res.data, checkPhone: phone.phone });
      })
      .catch((err: any) => {
        const msg = err?.response?.data?.error ?? 'Twilio check failed';
        this.setState({
          checkingId: null,
          checkResult: { status: 'not_found', message: msg },
          checkPhone: phone.phone,
        });
      });
  }

  handleTextFilter(field: keyof TwilioPhonesFilter, value: string, debounce = false) {
    const filter = this.state.filter;
    (filter as any)[field] = value || undefined;
    filter.page = undefined;
    this.setState({ filter });
    if (debounce) {
      if (this.timeouts[field]) clearTimeout(this.timeouts[field]);
      this.timeouts[field] = window.setTimeout(() => this.load(), 600);
    } else {
      this.load();
    }
  }

  flowBadge(flow: CallFlow): React.ReactElement {
    return (
      <span className={`${styles.Badge} ${FLOW_BADGE[flow] ?? ''}`}>
        {FLOW_LABEL[flow] ?? flow}
      </span>
    );
  }

  render() {
    const { phones, pagination, filter, departments } = this.state;

    return (
      <div>
        <Container maxWidth="lg">
          <Grid container spacing={1}>

            {/* Filters */}
            <Grid item xs={12} className={mainStyles.WorkerSkillsBack}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <div className={mainStyles.Header}>&nbsp;</div>
                  <div className={mainStyles.AgentName}>Twilio Phones</div>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <span className={styles.FilterLabel}>Phone number</span>
                  <PhoneMaskInput
                    className={styles.FilterControl}
                    value={this.state.phoneDisplay}
                    onChange={(masked, digits) => this.handlePhoneMask('phone', 'phoneDisplay', masked, digits)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <span className={styles.FilterLabel}>Location name</span>
                  <input
                    type="text"
                    placeholder="Search…"
                    className={styles.FilterControl}
                    value={filter.location ?? ''}
                    onChange={(e) => this.handleTextFilter('location', e.target.value, true)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <span className={styles.FilterLabel}>Forward to</span>
                  <PhoneMaskInput
                    className={styles.FilterControl}
                    value={this.state.forwardToDisplay}
                    onChange={(masked, digits) => this.handlePhoneMask('forward_to', 'forwardToDisplay', masked, digits)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <span className={styles.FilterLabel}>Flow</span>
                  <select
                    className={styles.FilterControl}
                    value={filter.call_flow ?? 'ALL'}
                    onChange={(e) => this.handleTextFilter('call_flow', e.target.value)}
                  >
                    <option value="ALL">ALL</option>
                    <option value="DEFAULT">DEFAULT</option>
                    <option value="CUSTOM">CUSTOM</option>
                    <option value="EXTERNAL">EXTERNAL</option>
                    <option value="CUSTOM_NEW_FLOW">CUSTOM NEW</option>
                  </select>
                </Grid>
                <Grid item xs={12} md={2}>
                  <span className={styles.FilterLabel}>Department</span>
                  <select
                    className={styles.FilterControl}
                    value={filter.department_id ?? ''}
                    onChange={(e) => this.handleTextFilter('department_id', e.target.value)}
                  >
                    <option value="">(all)</option>
                    <option value="0">— No location / dept —</option>
                    {departments.map((d) => (
                      <option key={d.id} value={String(d.id)}>{d.name}</option>
                    ))}
                  </select>
                </Grid>
              </Grid>
            </Grid>

            {/* Table */}
            <Grid item xs={12} className={mainStyles.WorkerSkillsBack}>
              <TableContainer component={Paper}>
                <Table aria-label="twilio phones table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Phone</TableCell>
                      <TableCell align="left">Location</TableCell>
                      <TableCell align="left">Department</TableCell>
                      <TableCell align="left">Flow</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {phones.map((row) => (
                      <TableRow key={row.id} className={!row.active ? styles.Inactive : ''}>
                        <TableCell align="left">
                          <div>{row.friendly()}</div>
                          <div className={styles.SubText}>{row.phone}</div>
                          {row.forwardCount > 0 && (
                            <button
                              className={styles.ForwardCount}
                              title="Show phones forwarding to this number"
                              onClick={() => this.searchForwardedTo(row.phone)}
                            >
                              ↑ {row.forwardCount} forwarding here
                            </button>
                          )}
                          <button
                            className={styles.CheckBtn}
                            disabled={this.state.checkingId === row.id}
                            onClick={() => this.checkInTwilio(row)}
                          >
                            {this.state.checkingId === row.id
                              ? <CircularProgress size={10} style={{ marginRight: 4 }} />
                              : '🔍'} Twilio
                          </button>
                        </TableCell>
                        <TableCell align="left">
                          <div>{row.locationName ?? '—'}</div>
                          {row.isPaid && <span className={styles.BadgePaid}>PAID</span>}
                        </TableCell>
                        <TableCell align="left">{row.departmentName ?? '—'}</TableCell>
                        <TableCell align="left">
                          {this.flowBadge(row.callFlow)}
                          {row.isCustomFlow() && row.forwardTo && (
                            <div
                              className={styles.ForwardTo}
                              title="Find this phone record"
                              onClick={() => this.searchByPhone(row.forwardTo!)}
                            >↳ {row.forwardTo}</div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {phones.length === 0 && !this.state.backdrop && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No phones found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {pagination !== null && (
                <Grid item xs={12}>
                  <Pagination
                    className={styles.Pagination}
                    count={pagination.LastPage}
                    page={pagination.CurrentPage}
                    onChange={(_e, page) => this.handleChangePage(page)}
                    variant="outlined"
                    shape="rounded"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Container>

        <Snackbar
          open={this.state.errorAlert}
          autoHideDuration={5000}
          onClose={() => this.setState({ errorAlert: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={() => this.setState({ errorAlert: false })} severity="error" variant="filled">
            Error<br />
            <div className={styles.AlertError}>{this.state.errors.join(', ')}</div>
          </Alert>
        </Snackbar>

        <Dialog open={!!this.state.checkResult} onClose={() => this.setState({ checkResult: null })}>
          <DialogTitle>
            Twilio Check — {this.state.checkPhone}
            <IconButton style={{ float: 'right' }} onClick={() => this.setState({ checkResult: null })}>
              <ClearIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ minWidth: 380 }}>
            {this.state.checkResult && (() => {
              const r = this.state.checkResult!;
              const cls =
                r.status === 'active'      ? styles.CheckResultActive :
                r.status === 'sid_changed' ? styles.CheckResultSidChange :
                r.status === 'released'    ? styles.CheckResultReleased :
                                             styles.CheckResultNotFound;
              return (
                <>
                  <div className={cls}>{r.message}</div>
                  {r.status === 'sid_changed' && (
                    <div className={styles.CheckDetail}>
                      Old SID: {r.old_sid}<br />
                      New SID: {r.new_sid}
                    </div>
                  )}
                  {(r.status === 'active' || r.status === 'released') && r.sid && (
                    <div className={styles.CheckDetail}>SID: {r.sid}</div>
                  )}
                </>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ checkResult: null })} color="primary">Close</Button>
          </DialogActions>
        </Dialog>

        <Backdrop className={styles.Backdrop} open={this.state.backdrop}>
          <CircularProgress className={styles.BackdropProgress} />&nbsp;&nbsp;
          <p className={styles.BackdropProgress}>Loading...</p>
        </Backdrop>
      </div>
    );
  }
}
